import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Box, Button } from '@mui/material';

import { Loading, ErrorMessage, StatusMessage, SimpleCheckbox } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { getImageBlob, releaseImageRef } from '@/src/utils';
import {
    appendFormData,
    extractImagesFromQuestion,
} from '@/src/pages/questions/createEditQuestion/services/questionServices';
import type { QuestionGenerationDTO } from '../dtos';

interface SaveQuizModalProps {
    open: boolean;
    title: string;
    mode: 'create' | 'edit';
    editModeId?: number;
    intendedGrades: number[];
    standardGrades: number[];
    questions: QuestionGenerationDTO[];
    onClose: () => void;
    onSubmitCallback?: () => void;
}

const SaveQuizModal: React.FC<SaveQuizModalProps> = ({
    open,
    title,
    mode,
    editModeId,
    intendedGrades,
    standardGrades,
    questions,
    onClose,
    onSubmitCallback,
}) => {
    const navigate = useNavigate();

    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const [shouldShuffleQuestions, setShouldShuffleQuestions] = useState(false);

    const handleAttemptClose = () => {
        if (submitting || success) return;
        onClose();
    };

    const handleSuccessClose = () => {
        navigate('/exams/yourQuizzes');
    };

    const handleSubmit = async (e: React.FormEvent<any>) => {
        e.preventDefault();
        if (submitting || success) return;
        if (!questions.length) {
            setError('No questions to save');
            return;
        }
        if (mode === 'edit' && !editModeId) {
            setError('Missing quiz ID for edit');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const payload = {
                title,
                intendedGrades,
                standardGrades,
                questions,
                shouldShuffleQuestions: false, // TODO: Implement
            };

            const imagesToProcess: string[] = [];
            questions.forEach((question) => {
                imagesToProcess.push(...extractImagesFromQuestion(question));
            });

            const formData = new FormData();
            appendFormData(formData, payload);
            for (const url of imagesToProcess) {
                const fileName = url.replace('local://', '');
                const blob = getImageBlob(fileName);
                if (!blob) continue;
                formData.append('images', blob, fileName);
            }
            if (imagesToProcess.length === 0) {
                formData.append('images', '');
            }

            const apiHeaders = { 'Content-Type': 'multipart/form-data' };
            if (mode === 'edit') {
                await apiRequest(
                    'PUT',
                    `assessments/exams/quizzes/${editModeId}`,
                    formData,
                    apiHeaders
                );
            } else {
                await apiRequest('POST', 'assessments/exams/quizzes', formData, apiHeaders);
            }

            for (const url of imagesToProcess) {
                releaseImageRef(url.replace('local://', ''));
            }

            setSuccess(true);
            if (onSubmitCallback) onSubmitCallback();
        } catch {
            setError(mode === 'edit' ? 'Failed to update quiz' : 'Failed to save quiz');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={handleAttemptClose}>
            {success ? (
                <Box
                    sx={{
                        p: 4,
                        bgcolor: 'background.paper',
                        margin: 'auto',
                        mt: 8,
                        width: 400,
                        textAlign: 'center',
                    }}
                >
                    <h2>{mode === 'edit' ? 'Quiz Updated!' : 'Quiz Created!'}</h2>
                    <Button variant="contained" onClick={handleSuccessClose} sx={{ mt: 2 }}>
                        Close
                    </Button>
                </Box>
            ) : (
                <Box sx={{ p: 4, bgcolor: 'background.paper', margin: 'auto', mt: 8, width: 400 }}>
                    <p>
                        {mode === 'edit' ? 'Update' : 'Create'} this quiz with {questions.length}{' '}
                        question
                        {questions.length === 1 ? '' : 's'}?
                    </p>
                    {title && (
                        <p>
                            <strong>Title:</strong> {title}
                        </p>
                    )}

                    <SimpleCheckbox
                        name="shuffleQuestions"
                        label="Shuffle question order for each attempt"
                        checked={shouldShuffleQuestions}
                        onChange={(e) => setShouldShuffleQuestions(e.target.checked)}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                        <Button
                            disabled={submitting || success}
                            color="error"
                            variant="outlined"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={submitting || success}
                            variant="contained"
                            onClick={handleSubmit}
                        >
                            {mode === 'edit' ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                    <ErrorMessage error={error} />
                    <StatusMessage
                        visible={success}
                        status={
                            mode === 'edit'
                                ? 'Quiz updated successfully!'
                                : 'Quiz created successfully!'
                        }
                        style={{ marginTop: 8 }}
                    />
                    <Loading
                        visible={submitting}
                        message={
                            mode === 'edit'
                                ? 'Updating... (this may take a while)'
                                : 'Creating... (this may take a while)'
                        }
                        size="sm"
                    />
                </Box>
            )}
        </Modal>
    );
};

export default SaveQuizModal;
